import { useEffect } from 'react';
import type { PurchaseHistory } from '../modules/DataTransformSchema';
import { ProductThumb } from './ProductThumb';
import { formatOrderDate } from '../utils/orderPresentation';

type OrderDetailOverlayProps = {
  order: PurchaseHistory;
  /** Mobile renders a bottom sheet; wider viewports render a centered modal. */
  variant: 'modal' | 'sheet';
  onClose: () => void;
};

export function OrderDetailOverlay({
  order,
  variant,
  onClose,
}: OrderDetailOverlayProps) {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const name = order.product_name || 'Unknown product';
  const price = order.product_price ?? order.order_total ?? '';
  const date = formatOrderDate(order);

  const detailRows = (
    <>
      {date && (
        <div className="flex justify-between text-[12.5px]">
          <span className="text-ink-muted">Date</span>
          <span className="font-medium text-ink">{date}</span>
        </div>
      )}
      {order.order_id && (
        <div className="flex justify-between gap-3 text-[12.5px]">
          <span className="text-ink-muted">Order ID</span>
          <span className="truncate font-mono text-ink">{order.order_id}</span>
        </div>
      )}
    </>
  );

  if (variant === 'sheet') {
    return (
      <>
        <div
          className="order-overlay-backdrop fixed inset-0 z-40 bg-ink/35"
          onClick={onClose}
          aria-hidden="true"
        />
        <div
          data-testid="order-detail-sheet"
          role="dialog"
          aria-modal="true"
          aria-label={name}
          className="order-detail-sheet fixed inset-x-0 bottom-0 z-50 flex max-h-[82%] flex-col overflow-hidden rounded-t-[24px] bg-surface shadow-[0_-12px_32px_rgba(0,0,0,0.14)]"
        >
          <div className="flex flex-shrink-0 justify-center pt-2.5">
            <div className="h-1 w-9 rounded-full bg-rule" />
          </div>
          <div className="flex gap-4 overflow-y-auto px-6 pb-7 pt-4">
            <ProductThumb
              order={order}
              className="h-24 w-24 flex-shrink-0 rounded-2xl text-[32px]"
            />
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <b className="text-[15.5px] font-bold leading-[1.4] text-ink">
                {name}
              </b>
              <div className="flex flex-wrap items-center gap-2.5">
                {price && (
                  <div className="font-display text-[19px] font-bold text-ink">
                    {price}
                  </div>
                )}
                {order.shipment_status && (
                  <span className="rounded-full bg-primary-pale px-2.5 py-1 text-[11px] font-semibold text-primary">
                    {order.shipment_status}
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-1.5 border-t border-rule-light pt-1.5">
                {detailRows}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div
      className="order-overlay-backdrop fixed inset-0 z-40 flex items-center justify-center bg-ink/45 p-6"
      onClick={onClose}
    >
      <div
        data-testid="order-detail-modal"
        role="dialog"
        aria-modal="true"
        aria-label={name}
        onClick={(event) => event.stopPropagation()}
        className="order-detail-modal flex max-h-[90%] w-[min(420px,100%)] flex-col overflow-y-auto rounded-[20px] bg-surface shadow-[0_24px_60px_rgba(0,0,0,0.25)]"
      >
        <div className="flex justify-end p-3.5 pb-0">
          <button
            data-testid="order-detail-close-button"
            onClick={onClose}
            aria-label="Close details"
            className="h-7 w-7 rounded-full bg-paper-warm text-sm leading-none text-ink transition-colors hover:bg-rule"
          >
            ✕
          </button>
        </div>
        <div className="flex flex-col gap-4 px-7 pb-7 pt-2">
          <ProductThumb
            order={order}
            className="aspect-[16/9] w-full rounded-2xl text-[40px]"
          />
          <b className="text-[17px] font-bold leading-[1.4] text-ink">{name}</b>
          <div className="flex items-center justify-between gap-3">
            {price && (
              <div className="font-display text-2xl font-bold text-ink">
                {price}
              </div>
            )}
            {order.shipment_status && (
              <span className="rounded-full bg-primary-pale px-3 py-[5px] text-[11.5px] font-semibold text-primary">
                {order.shipment_status}
              </span>
            )}
          </div>
          <div className="flex flex-col gap-2 border-t border-rule-light pt-3.5">
            {detailRows}
          </div>
        </div>
      </div>
    </div>
  );
}
